import { castArray } from 'lodash/fp';

type GraphNode = string;
type GraphNodes = GraphNode[];

enum Direction {
    UP,
    DOWN
}

type GraphEdges = {
    from: GraphNode;
    to: GraphNode;
}[];

const inNodeOrder = (
    adjacencies: Map<GraphNode, Set<GraphNode>>,
    indexByNode: Map<GraphNode, number>
) =>
    new Map<GraphNode, GraphNodes>(
        [...adjacencies].map(([node, neighbours]) => [
            node,
            [...neighbours].sort((a, b) => indexByNode.get(a)! - indexByNode.get(b)!)
        ])
    );

export class Graph {
    #indexByNode = new Map<GraphNode, number>();
    #targetsByNode = new Map<GraphNode, GraphNodes>();
    #sourcesByNode = new Map<GraphNode, GraphNodes>();

    nodes: GraphNodes;
    edges: GraphEdges;

    constructor(nodes?: GraphNodes, edges?: GraphEdges) {
        this.nodes = nodes ?? [];
        this.edges = edges ?? [];
        this._buildAdjacencies();
    }

    _buildAdjacencies() {
        this.nodes.forEach((node, index) => {
            if (!this.#indexByNode.has(node)) {
                this.#indexByNode.set(node, index);
            }
        });

        const targets = new Map<GraphNode, Set<GraphNode>>();
        const sources = new Map<GraphNode, Set<GraphNode>>();

        this.nodes.forEach((node) => {
            targets.set(node, new Set());
            sources.set(node, new Set());
        });

        this.edges.forEach(({ from, to }) => {
            if (!this.#indexByNode.has(from) || !this.#indexByNode.has(to)) {
                return;
            }

            targets.get(from)!.add(to);
            sources.get(to)!.add(from);
        });

        this.#targetsByNode = inNodeOrder(targets, this.#indexByNode);
        this.#sourcesByNode = inNodeOrder(sources, this.#indexByNode);
    }

    isEmpty() {
        return this.nodes.length === 0;
    }

    _indexOf(node: GraphNode) {
        return this.#indexByNode.get(node) ?? -1;
    }

    _nodeAt(index: number) {
        return this.nodes[index];
    }

    _getTargets(node: GraphNode) {
        return this.#targetsByNode.get(node)?.slice() ?? [];
    }

    _getSources(node: GraphNode) {
        return this.#sourcesByNode.get(node)?.slice() ?? [];
    }

    _getConnectedNodes(start: GraphNode, direction: Direction) {
        const visited = new Set<GraphNode>();
        const stack = [start];

        while (stack.length > 0) {
            const node = stack.pop();

            if (node && !visited.has(node)) {
                visited.add(node);

                const neighbours =
                    direction === Direction.DOWN ? this._getTargets(node) : this._getSources(node);

                neighbours.forEach((neighbour) => {
                    if (!visited.has(neighbour)) {
                        stack.push(neighbour);
                    }
                });
            }
        }

        return [...visited];
    }

    findAllPaths({ from, to }: { from: GraphNode; to: GraphNode }) {
        function findPaths(
            adjacencies: Record<GraphNode, GraphNodes>,
            start: GraphNode,
            end: GraphNode,
            path: GraphNodes = []
        ) {
            const currentPath = path.concat([start]);

            if (start === end) {
                return [currentPath];
            }

            const paths: GraphNode[][] = [];

            adjacencies[start]!.forEach((neighbor) => {
                if (!currentPath.includes(neighbor)) {
                    const newPaths = findPaths(adjacencies, neighbor, end, currentPath);

                    newPaths.forEach((newPaths) => {
                        paths.push(newPaths);
                    });
                }
            });

            return paths;
        }

        const adjacencies: Record<GraphNode, GraphNodes> = {};

        this.#targetsByNode.forEach((targets, node) => {
            adjacencies[node] = targets.slice();
        });

        const paths = findPaths(adjacencies, from, to);

        if (paths.length !== 0) {
            return paths;
        }

        return findPaths(adjacencies, to, from);
    }

    getDownstreamNodes(node: GraphNode) {
        return node ? this._getConnectedNodes(node, Direction.DOWN).slice(1) : [];
    }

    getUpstreamNodes(node: GraphNode) {
        return node ? this._getConnectedNodes(node, Direction.UP).slice(1) : [];
    }

    getConnectedNodes(
        nodes: GraphNode | GraphNodes,
        dir: 'upstream' | 'downstream' | 'all' = 'all'
    ) {
        const connectedNodes = castArray(nodes).map((node) => [
            ...(dir !== 'upstream' ? this.getDownstreamNodes(node) : []),
            node,
            ...(dir !== 'downstream' ? this.getUpstreamNodes(node) : [])
        ]);

        return [...new Set(connectedNodes.flat())];
    }

    group(id: GraphNode, groupNodes: GraphNodes) {
        const newNodes = [...this.nodes.filter((node) => !groupNodes.includes(node)), id];
        const newEdges: GraphEdges = [];

        this.edges.forEach((edge) => {
            const isSourceInGroup = groupNodes.includes(edge.from);
            const isTargetInGroup = groupNodes.includes(edge.to);
            if (isSourceInGroup && isTargetInGroup) {
                return;
            }

            if (isSourceInGroup) {
                if (!newEdges.find((e) => e.from === id && e.to === edge.to)) {
                    newEdges.push({ from: id, to: edge.to });
                }
            } else if (isTargetInGroup) {
                if (!newEdges.find((e) => e.from === edge.from && e.to === id)) {
                    newEdges.push({ from: edge.from, to: id });
                }
            } else {
                newEdges.push({ ...edge });
            }
        });

        return new Graph(newNodes, newEdges);
    }

    groupAll(groups: { id: GraphNode; children: GraphNode[] }[]) {
        let updatedNodes = [...this.nodes];
        let updatedEdges: GraphEdges = [...this.edges];

        groups.forEach(({ id, children }) => {
            updatedNodes = updatedNodes.filter((node) => !children.includes(node));

            if (!updatedNodes.some((node) => node === id)) {
                updatedNodes.push(id);
            }

            const newEdges: GraphEdges = [];

            updatedEdges.forEach((edge) => {
                const isSourceInGroup = children.includes(edge.from);
                const isTargetInGroup = children.includes(edge.to);

                if (isSourceInGroup && isTargetInGroup) {
                    return;
                }
                if (isSourceInGroup) {
                    if (!newEdges.some((e) => e.from === id && e.to === edge.to)) {
                        newEdges.push({ from: id, to: edge.to });
                    }
                } else if (isTargetInGroup) {
                    if (!newEdges.some((e) => e.from === edge.from && e.to === id)) {
                        newEdges.push({ from: edge.from, to: id });
                    }
                } else {
                    newEdges.push({ ...edge });
                }
            });

            updatedEdges = [...newEdges];
        });

        return new Graph(updatedNodes, updatedEdges);
    }

    getRootNodes() {
        const targets = new Set(this.edges.map(({ to }) => to));

        return this.nodes.filter((node) => !targets.has(node));
    }

    getLeafNodes() {
        const sources = new Set(this.edges.map(({ from }) => from));

        return this.nodes.filter((node) => !sources.has(node));
    }

    compress() {
        if (this.isEmpty()) {
            return this;
        }

        const targets = new Map(this.nodes.map((node) => [node, new Set(this._getTargets(node))]));
        const sources = new Map(this.nodes.map((node) => [node, new Set(this._getSources(node))]));

        for (let i = 0; i < this.nodes.length; i++) {
            const node = this._nodeAt(i)!;
            const outgoing = targets.get(node)!;
            const incoming = sources.get(node)!;

            if (outgoing.size === 1 && incoming.size === 1) {
                const target = [...outgoing][0]!;
                const source = [...incoming][0]!;

                targets.get(source)!.delete(node);
                sources.get(node)!.delete(source);

                targets.get(node)!.delete(target);
                sources.get(target)!.delete(node);

                targets.get(source)!.add(target);
                sources.get(target)!.add(source);

                i = -1;
            }
        }

        const nodes = this.nodes.filter(
            (node) => targets.get(node)!.size > 0 || sources.get(node)!.size > 0
        );

        const edges: GraphEdges = [];
        const orderedTargets = inNodeOrder(targets, this.#indexByNode);

        this.nodes.forEach((from) => {
            orderedTargets.get(from)!.forEach((to) => edges.push({ from, to }));
        });

        return new Graph(nodes, edges);
    }

    countCompressedNodes(id: GraphNode) {
        const countSingles = (adjacencies: Map<GraphNode, GraphNodes>) => (start: GraphNode) => {
            let count = 0;
            let current: GraphNode | undefined = start;

            while (current !== undefined) {
                const neighbours = adjacencies.get(current);

                if (neighbours?.length === 1) {
                    count++;
                    current = neighbours[0];
                } else {
                    current = undefined;
                }
            }

            return count;
        };

        const countSinglesDown = countSingles(this.#targetsByNode);
        const countSinglesUp = countSingles(this.#sourcesByNode);

        const counts = [
            ...this._getSources(id).map(countSinglesUp),
            ...this._getTargets(id).map(countSinglesDown)
        ];

        return 1 + counts.reduce((total, count) => total + count, 0);
    }

    isAcyclic() {
        // uses a depth first search to find cycles
        // WHITE: node hasn't been visited
        // GREY: node has been visited but downstream nodes have not
        // BLACK: node and all downstream nodes have been visited
        // cycles exist when grey nodes are revisited by the search
        enum State {
            WHITE,
            GREY,
            BLACK
        }

        let hasCycle = false;

        if (this.isEmpty()) {
            return true;
        }

        const visited = this.nodes.reduce((acc, node) => {
            acc.set(node, State.WHITE);

            return acc;
        }, new Map());

        const roots = this.getRootNodes();

        if (roots.length === 0) {
            return false;
        }

        const dfs = (graph: Graph, node: GraphNode) => {
            visited.set(node, State.GREY);

            graph._getTargets(node).forEach((target) => {
                if (visited.get(target) === State.GREY) {
                    hasCycle = true;
                    return;
                }

                if (visited.get(target) === State.WHITE) {
                    dfs(graph, target);
                }
            });

            visited.set(node, State.BLACK);
        };

        roots.forEach((root) => dfs(this, root));

        const visitedEveryNode = Array.from(visited.values()).every((c) => c !== State.WHITE);

        return !hasCycle && visitedEveryNode;
    }

    toDot() {
        return `digraph G {
            ${this.edges.map(({ from, to }) => `${from} -> ${to}`).join('\n')}
        }`;
    }

    subgraph(...nodes: GraphNodes) {
        const connectedNodes = this.getConnectedNodes(nodes);
        const edges = this.edges.filter(
            ({ from, to }) => connectedNodes.includes(from) && connectedNodes.includes(to)
        );

        return new Graph(connectedNodes, edges);
    }
}
